// ask-storage.ts — per-user persistence for the Ask thread.
//
// The AskTab's chat state used to be in-memory only, so navigating to
// another tab (Live data, Documents) or quitting the app threw the
// conversation away. Cheap localStorage write-through fixes both: the
// thread survives tab switches inside the same session AND restarts.
//
// We key by user email so two operators signed in on the same machine
// don't see each other's history. We version the payload so future
// schema changes (e.g. richer citation shape) can migrate cleanly. We
// cap at MAX_PERSISTED to keep the storage budget under control — a
// runaway daily ritual of "ask 50 questions" is fine, but ten years of
// it shouldn't blow the localStorage quota.
//
// Only completed turns persist. In-flight streaming messages and empty
// placeholders are dropped before save — restoring a half-streamed
// "Thinking…" would be confusing UX, and the actual answer is lost
// anyway (Anthropic stream broke when the tab unmounted).

import type { Citation } from "./ask";

const STORAGE_VERSION = 1;
const STORAGE_KEY_PREFIX = "bvndle.ask.thread";
/** Hard cap on persisted turns. ~50 Q/A pairs is plenty for a working
 * conversation; older turns silently roll off the back. */
const MAX_PERSISTED = 100;

/** The subset of ChatMessage that survives a round-trip to localStorage.
 * Streaming-only fields (stage, streaming) are intentionally excluded —
 * they're transient. */
export interface PersistedMessage {
  role: "user" | "assistant";
  content: string;
  citations?: Citation[];
  error?: string;
}

interface PersistedThread {
  v: number;
  messages: PersistedMessage[];
  savedAt: string;
}

function storageKey(userEmail: string | null | undefined): string {
  const id = userEmail ? userEmail.toLowerCase() : "anon";
  return `${STORAGE_KEY_PREFIX}:${id}`;
}

/**
 * Load the persisted thread for this user. Returns [] if nothing is
 * stored, the payload is malformed, or the schema version doesn't
 * match (future-proofing for schema bumps).
 */
export function loadAskThread(
  userEmail: string | null | undefined,
): PersistedMessage[] {
  try {
    const raw = localStorage.getItem(storageKey(userEmail));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PersistedThread;
    if (parsed?.v !== STORAGE_VERSION) return [];
    if (!Array.isArray(parsed.messages)) return [];
    return parsed.messages.filter(
      (m) => m && (m.role === "user" || m.role === "assistant") && typeof m.content === "string",
    );
  } catch {
    return [];
  }
}

/**
 * Save the current thread (subset of ChatMessage[]) to localStorage.
 * Best-effort: silently logs and returns on quota errors or disabled
 * storage rather than throwing — chat itself must keep working.
 */
export function saveAskThread(
  userEmail: string | null | undefined,
  messages: PersistedMessage[],
): void {
  try {
    const trimmed = messages.slice(-MAX_PERSISTED);
    if (trimmed.length === 0) {
      localStorage.removeItem(storageKey(userEmail));
      return;
    }
    const payload: PersistedThread = {
      v: STORAGE_VERSION,
      messages: trimmed,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(storageKey(userEmail), JSON.stringify(payload));
  } catch (e) {
    console.warn("[ask-storage] save failed (storage full or disabled?)", e);
  }
}

/** Wipe the persisted thread for this user — used by "New conversation". */
export function clearAskThread(userEmail: string | null | undefined): void {
  try {
    localStorage.removeItem(storageKey(userEmail));
  } catch {
    /* swallow — same reasoning as save */
  }
}
